import {
  createUseReadContract,
  createUseWriteContract,
  createUseSimulateContract,
  createUseWatchContractEvent,
} from 'wagmi/codegen'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Erc20
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const erc20Abi = [
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [],
    name: 'name',
    outputs: [{ name: '', type: 'string' }],
    stateMutability: 'view',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [],
    name: 'totalSupply',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: '_from', type: 'address' },
      { name: '_to', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'transferFrom',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [],
    name: 'decimals',
    outputs: [{ name: '', type: 'uint8' }],
    stateMutability: 'view',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [],
    name: 'symbol',
    outputs: [{ name: '', type: 'string' }],
    stateMutability: 'view',
  },
  {
    constant: false,
    payable: false,
    type: 'function',
    inputs: [
      { name: '_to', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
  },
  {
    constant: true,
    payable: false,
    type: 'function',
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
  },
  { payable: true, type: 'fallback', stateMutability: 'payable' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'owner', type: 'address', indexed: true },
      { name: 'spender', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Approval',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: 'from', type: 'address', indexed: true },
      { name: 'to', type: 'address', indexed: true },
      { name: 'value', type: 'uint256', indexed: false },
    ],
    name: 'Transfer',
  },
] as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Escrow
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const escrowAbi = [
  {
    type: 'constructor',
    inputs: [
      { name: '_feeCollector', internalType: 'address', type: 'address' },
      {
        name: '_seller',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: '_buyer',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: '_duration', internalType: 'uint64', type: 'uint64' },
    ],
    stateMutability: 'nonpayable',
  },
  { type: 'error', inputs: [], name: 'AlreadyDeposited' },
  { type: 'error', inputs: [], name: 'BadParams' },
  { type: 'error', inputs: [], name: 'DurationZero' },
  {
    type: 'error',
    inputs: [
      { name: 'expected', internalType: 'enum Escrow.Status', type: 'uint8' },
      { name: 'current', internalType: 'enum Escrow.Status', type: 'uint8' },
    ],
    name: 'InvalidStatus',
  },
  { type: 'error', inputs: [], name: 'NotParty' },
  { type: 'error', inputs: [], name: 'ReentrancyGuardReentrantCall' },
  {
    type: 'error',
    inputs: [{ name: 'token', internalType: 'address', type: 'address' }],
    name: 'SafeERC20FailedOperation',
  },
  { type: 'error', inputs: [], name: 'TooEarly' },
  { type: 'error', inputs: [], name: 'TooLate' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'party',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'exchToken',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'exchAmount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
      {
        name: 'feeToken',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'feeAmount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'AssetsDeposited',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'startTime',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
      {
        name: 'duration',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
    ],
    name: 'DealActivated',
  },
  { type: 'event', anonymous: false, inputs: [], name: 'DealApproved' },
  { type: 'event', anonymous: false, inputs: [], name: 'DealCancelled' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'seller',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'buyer',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'duration',
        internalType: 'uint64',
        type: 'uint64',
        indexed: false,
      },
    ],
    name: 'DealCreated',
  },
  { type: 'event', anonymous: false, inputs: [], name: 'DealExpired' },
  { type: 'event', anonymous: false, inputs: [], name: 'DealRejected' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'party',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: false,
      },
      {
        name: 'amount',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'PenaltyDeposited',
  },
  {
    type: 'function',
    inputs: [],
    name: 'approve',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'buyer',
    outputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'exchangeToken', internalType: 'address', type: 'address' },
      { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
      { name: 'feeToken', internalType: 'address', type: 'address' },
      { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
      { name: 'penaltyToken', internalType: 'address', type: 'address' },
      { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'cancelBeforeStart',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'deadline',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'depositAssets',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'depositPenalty',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'duration',
    outputs: [{ name: '', internalType: 'uint64', type: 'uint64' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'expire',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'feeCollector',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: 'who', internalType: 'address', type: 'address' }],
    name: 'isParty',
    outputs: [
      { name: 'isSeller', internalType: 'bool', type: 'bool' },
      { name: 'isBuyer', internalType: 'bool', type: 'bool' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'partyStates',
    outputs: [
      {
        name: 's',
        internalType: 'struct Escrow.PartyState',
        type: 'tuple',
        components: [
          { name: 'penaltyDeposited', internalType: 'bool', type: 'bool' },
          { name: 'assetsDeposited', internalType: 'bool', type: 'bool' },
        ],
      },
      {
        name: 'b',
        internalType: 'struct Escrow.PartyState',
        type: 'tuple',
        components: [
          { name: 'penaltyDeposited', internalType: 'bool', type: 'bool' },
          { name: 'assetsDeposited', internalType: 'bool', type: 'bool' },
        ],
      },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'reject',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'seller',
    outputs: [
      { name: 'account', internalType: 'address', type: 'address' },
      { name: 'exchangeToken', internalType: 'address', type: 'address' },
      { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
      { name: 'feeToken', internalType: 'address', type: 'address' },
      { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
      { name: 'penaltyToken', internalType: 'address', type: 'address' },
      { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'startTime',
    outputs: [{ name: '', internalType: 'uint64', type: 'uint64' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'status',
    outputs: [{ name: '', internalType: 'enum Escrow.Status', type: 'uint8' }],
    stateMutability: 'view',
  },
] as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// EscrowFactory
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const escrowFactoryAbi = [
  {
    type: 'constructor',
    inputs: [
      { name: '_initialOwner', internalType: 'address', type: 'address' },
      { name: '_initialController', internalType: 'address', type: 'address' },
      {
        name: '_initialFeeCollector',
        internalType: 'address',
        type: 'address',
      },
    ],
    stateMutability: 'nonpayable',
  },
  { type: 'error', inputs: [], name: 'EscrowFactoryInvalidTokenAddress' },
  {
    type: 'error',
    inputs: [{ name: 'sender', internalType: 'address', type: 'address' }],
    name: 'EscrowFactoryNotController',
  },
  {
    type: 'error',
    inputs: [{ name: 'sender', internalType: 'address', type: 'address' }],
    name: 'EscrowFactoryNotOwner',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'escrowAddress',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'sellerParty',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
      {
        name: 'buyerParty',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
        indexed: false,
      },
      {
        name: 'expirationDate',
        internalType: 'uint256',
        type: 'uint256',
        indexed: false,
      },
    ],
    name: 'EscrowCreated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'TokenUnwhitelisted',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'token',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
    ],
    name: 'TokenWhitelisted',
  },
  {
    type: 'function',
    inputs: [{ name: '_token', internalType: 'address', type: 'address' }],
    name: 'addWhitelistedToken',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'controller',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [
      {
        name: 'sellerPartyC',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
      },
      {
        name: 'buyerPartyC',
        internalType: 'struct PartyTerms',
        type: 'tuple',
        components: [
          { name: 'account', internalType: 'address', type: 'address' },
          { name: 'exchangeToken', internalType: 'address', type: 'address' },
          { name: 'exchangeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'feeToken', internalType: 'address', type: 'address' },
          { name: 'feeAmount', internalType: 'uint256', type: 'uint256' },
          { name: 'penaltyToken', internalType: 'address', type: 'address' },
          { name: 'penaltyAmount', internalType: 'uint256', type: 'uint256' },
        ],
      },
      { name: 'duration', internalType: 'uint64', type: 'uint64' },
    ],
    name: 'createEscrow',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [],
    name: 'feeCollector',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [],
    name: 'owner',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    inputs: [{ name: '_token', internalType: 'address', type: 'address' }],
    name: 'removeWhitelistedToken',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    type: 'function',
    inputs: [{ name: '', internalType: 'address', type: 'address' }],
    name: 'whitelistedTokens',
    outputs: [{ name: '', internalType: 'bool', type: 'bool' }],
    stateMutability: 'view',
  },
] as const

export const escrowFactoryAddress =
  '0xf0F4bd4bdE4A9b43F6E6f6CC39d6C04a5cbc8279' as const

export const escrowFactoryConfig = {
  address: escrowFactoryAddress,
  abi: escrowFactoryAbi,
} as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// React
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useReadErc20 = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"name"`
 */
export const useReadErc20Name = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'name',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"totalSupply"`
 */
export const useReadErc20TotalSupply = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'totalSupply',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"decimals"`
 */
export const useReadErc20Decimals = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'decimals',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"balanceOf"`
 */
export const useReadErc20BalanceOf = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'balanceOf',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"symbol"`
 */
export const useReadErc20Symbol = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'symbol',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"allowance"`
 */
export const useReadErc20Allowance = /*#__PURE__*/ createUseReadContract({
  abi: erc20Abi,
  functionName: 'allowance',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useWriteErc20 = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"approve"`
 */
export const useWriteErc20Approve = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'approve',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transferFrom"`
 */
export const useWriteErc20TransferFrom = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'transferFrom',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transfer"`
 */
export const useWriteErc20Transfer = /*#__PURE__*/ createUseWriteContract({
  abi: erc20Abi,
  functionName: 'transfer',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__
 */
export const useSimulateErc20 = /*#__PURE__*/ createUseSimulateContract({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"approve"`
 */
export const useSimulateErc20Approve = /*#__PURE__*/ createUseSimulateContract({
  abi: erc20Abi,
  functionName: 'approve',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transferFrom"`
 */
export const useSimulateErc20TransferFrom =
  /*#__PURE__*/ createUseSimulateContract({
    abi: erc20Abi,
    functionName: 'transferFrom',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link erc20Abi}__ and `functionName` set to `"transfer"`
 */
export const useSimulateErc20Transfer = /*#__PURE__*/ createUseSimulateContract(
  { abi: erc20Abi, functionName: 'transfer' },
)

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__
 */
export const useWatchErc20Event = /*#__PURE__*/ createUseWatchContractEvent({
  abi: erc20Abi,
})

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__ and `eventName` set to `"Approval"`
 */
export const useWatchErc20ApprovalEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20Abi,
    eventName: 'Approval',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link erc20Abi}__ and `eventName` set to `"Transfer"`
 */
export const useWatchErc20TransferEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: erc20Abi,
    eventName: 'Transfer',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__
 */
export const useReadEscrow = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"buyer"`
 */
export const useReadEscrowBuyer = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'buyer',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"deadline"`
 */
export const useReadEscrowDeadline = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'deadline',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"duration"`
 */
export const useReadEscrowDuration = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'duration',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"feeCollector"`
 */
export const useReadEscrowFeeCollector = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'feeCollector',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"isParty"`
 */
export const useReadEscrowIsParty = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'isParty',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"partyStates"`
 */
export const useReadEscrowPartyStates = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'partyStates',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"seller"`
 */
export const useReadEscrowSeller = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'seller',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"startTime"`
 */
export const useReadEscrowStartTime = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'startTime',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"status"`
 */
export const useReadEscrowStatus = /*#__PURE__*/ createUseReadContract({
  abi: escrowAbi,
  functionName: 'status',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__
 */
export const useWriteEscrow = /*#__PURE__*/ createUseWriteContract({
  abi: escrowAbi,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"approve"`
 */
export const useWriteEscrowApprove = /*#__PURE__*/ createUseWriteContract({
  abi: escrowAbi,
  functionName: 'approve',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"cancelBeforeStart"`
 */
export const useWriteEscrowCancelBeforeStart =
  /*#__PURE__*/ createUseWriteContract({
    abi: escrowAbi,
    functionName: 'cancelBeforeStart',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"depositAssets"`
 */
export const useWriteEscrowDepositAssets = /*#__PURE__*/ createUseWriteContract(
  { abi: escrowAbi, functionName: 'depositAssets' },
)

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"depositPenalty"`
 */
export const useWriteEscrowDepositPenalty =
  /*#__PURE__*/ createUseWriteContract({
    abi: escrowAbi,
    functionName: 'depositPenalty',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"expire"`
 */
export const useWriteEscrowExpire = /*#__PURE__*/ createUseWriteContract({
  abi: escrowAbi,
  functionName: 'expire',
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"reject"`
 */
export const useWriteEscrowReject = /*#__PURE__*/ createUseWriteContract({
  abi: escrowAbi,
  functionName: 'reject',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__
 */
export const useSimulateEscrow = /*#__PURE__*/ createUseSimulateContract({
  abi: escrowAbi,
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"approve"`
 */
export const useSimulateEscrowApprove = /*#__PURE__*/ createUseSimulateContract(
  { abi: escrowAbi, functionName: 'approve' },
)

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"cancelBeforeStart"`
 */
export const useSimulateEscrowCancelBeforeStart =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowAbi,
    functionName: 'cancelBeforeStart',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"depositAssets"`
 */
export const useSimulateEscrowDepositAssets =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowAbi,
    functionName: 'depositAssets',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"depositPenalty"`
 */
export const useSimulateEscrowDepositPenalty =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowAbi,
    functionName: 'depositPenalty',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"expire"`
 */
export const useSimulateEscrowExpire = /*#__PURE__*/ createUseSimulateContract({
  abi: escrowAbi,
  functionName: 'expire',
})

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowAbi}__ and `functionName` set to `"reject"`
 */
export const useSimulateEscrowReject = /*#__PURE__*/ createUseSimulateContract({
  abi: escrowAbi,
  functionName: 'reject',
})

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__
 */
export const useWatchEscrowEvent = /*#__PURE__*/ createUseWatchContractEvent({
  abi: escrowAbi,
})

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"AssetsDeposited"`
 */
export const useWatchEscrowAssetsDepositedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'AssetsDeposited',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealActivated"`
 */
export const useWatchEscrowDealActivatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealActivated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealApproved"`
 */
export const useWatchEscrowDealApprovedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealApproved',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealCancelled"`
 */
export const useWatchEscrowDealCancelledEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealCancelled',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealCreated"`
 */
export const useWatchEscrowDealCreatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealCreated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealExpired"`
 */
export const useWatchEscrowDealExpiredEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealExpired',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"DealRejected"`
 */
export const useWatchEscrowDealRejectedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'DealRejected',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowAbi}__ and `eventName` set to `"PenaltyDeposited"`
 */
export const useWatchEscrowPenaltyDepositedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowAbi,
    eventName: 'PenaltyDeposited',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowFactoryAbi}__
 */
export const useReadEscrowFactory = /*#__PURE__*/ createUseReadContract({
  abi: escrowFactoryAbi,
  address: escrowFactoryAddress,
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"controller"`
 */
export const useReadEscrowFactoryController =
  /*#__PURE__*/ createUseReadContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'controller',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"feeCollector"`
 */
export const useReadEscrowFactoryFeeCollector =
  /*#__PURE__*/ createUseReadContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'feeCollector',
  })

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"owner"`
 */
export const useReadEscrowFactoryOwner = /*#__PURE__*/ createUseReadContract({
  abi: escrowFactoryAbi,
  address: escrowFactoryAddress,
  functionName: 'owner',
})

/**
 * Wraps __{@link useReadContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"whitelistedTokens"`
 */
export const useReadEscrowFactoryWhitelistedTokens =
  /*#__PURE__*/ createUseReadContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'whitelistedTokens',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowFactoryAbi}__
 */
export const useWriteEscrowFactory = /*#__PURE__*/ createUseWriteContract({
  abi: escrowFactoryAbi,
  address: escrowFactoryAddress,
})

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"addWhitelistedToken"`
 */
export const useWriteEscrowFactoryAddWhitelistedToken =
  /*#__PURE__*/ createUseWriteContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'addWhitelistedToken',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"createEscrow"`
 */
export const useWriteEscrowFactoryCreateEscrow =
  /*#__PURE__*/ createUseWriteContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'createEscrow',
  })

/**
 * Wraps __{@link useWriteContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"removeWhitelistedToken"`
 */
export const useWriteEscrowFactoryRemoveWhitelistedToken =
  /*#__PURE__*/ createUseWriteContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'removeWhitelistedToken',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowFactoryAbi}__
 */
export const useSimulateEscrowFactory = /*#__PURE__*/ createUseSimulateContract(
  { abi: escrowFactoryAbi, address: escrowFactoryAddress },
)

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"addWhitelistedToken"`
 */
export const useSimulateEscrowFactoryAddWhitelistedToken =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'addWhitelistedToken',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"createEscrow"`
 */
export const useSimulateEscrowFactoryCreateEscrow =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'createEscrow',
  })

/**
 * Wraps __{@link useSimulateContract}__ with `abi` set to __{@link escrowFactoryAbi}__ and `functionName` set to `"removeWhitelistedToken"`
 */
export const useSimulateEscrowFactoryRemoveWhitelistedToken =
  /*#__PURE__*/ createUseSimulateContract({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    functionName: 'removeWhitelistedToken',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowFactoryAbi}__
 */
export const useWatchEscrowFactoryEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowFactoryAbi}__ and `eventName` set to `"EscrowCreated"`
 */
export const useWatchEscrowFactoryEscrowCreatedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    eventName: 'EscrowCreated',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowFactoryAbi}__ and `eventName` set to `"TokenUnwhitelisted"`
 */
export const useWatchEscrowFactoryTokenUnwhitelistedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    eventName: 'TokenUnwhitelisted',
  })

/**
 * Wraps __{@link useWatchContractEvent}__ with `abi` set to __{@link escrowFactoryAbi}__ and `eventName` set to `"TokenWhitelisted"`
 */
export const useWatchEscrowFactoryTokenWhitelistedEvent =
  /*#__PURE__*/ createUseWatchContractEvent({
    abi: escrowFactoryAbi,
    address: escrowFactoryAddress,
    eventName: 'TokenWhitelisted',
  })
